{
  'use strict';

  const initCarousel = (card) => {
    if (card.dataset.mxCarouselInitialized === 'true') return;

    const track = card.querySelector('[data-mx-carousel-track]');
    const dots = [...card.querySelectorAll('[data-mx-carousel-dot]')];
    const slides = [...card.querySelectorAll('[data-mx-carousel-slide]')];
    if (!track || !dots.length) return;

    card.dataset.mxCarouselInitialized = 'true';

    const setActive = (index) => {
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      });
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.toggleAttribute('inert', !active);
        if (active) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });
    };

    let cancelSnap = null;
    const stopSnap = () => {
      if (cancelSnap) track.scrollTo({ left: track.scrollLeft, behavior: 'auto' });
      cancelSnap?.();
      cancelSnap = null;
      track.classList.remove('is-snapping');
    };

    dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        stopSnap();
        track.scrollTo({ left: track.clientWidth * index, behavior: 'smooth' });
        setActive(index);
      });
    });

    const shortSwipeDuration = 300;
    const longSwipeRatio = 0.5;

    window.MXTheme.initializeHorizontalDragScroll(track, {
      beforeStart: stopSnap,
      getDragData: () => ({ startIndex: Math.round(track.scrollLeft / track.clientWidth) }),
      onDragEnd: ({ data, duration, scrollLeft, startScrollLeft }) => {
        const { clientWidth } = track;
        if (!clientWidth) return;

        const distance = scrollLeft - startScrollLeft;
        const direction = Math.sign(distance);
        const isShortSwipe = duration < shortSwipeDuration;
        const passedLongSwipeRatio = Math.abs(distance) / clientWidth >= longSwipeRatio;
        const shouldAdvance = direction !== 0 && (isShortSwipe || passedLongSwipeRatio);
        const targetIndex = shouldAdvance ? data.startIndex + direction : data.startIndex;
        const index = Math.max(0, Math.min(slides.length - 1, targetIndex));
        const targetLeft = clientWidth * index;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const canTrackSmoothScroll = !reduced && 'onscrollend' in track;

        if (!canTrackSmoothScroll || Math.abs(scrollLeft - targetLeft) <= 1) {
          track.scrollTo({ left: targetLeft, behavior: 'auto' });
          setActive(index);
          return;
        }

        track.classList.add('is-snapping');
        const finishSnap = () => {
          if (Math.abs(track.scrollLeft - targetLeft) > 1) return;
          stopSnap();
          setActive(index);
        };
        cancelSnap = () => {
          track.removeEventListener('scrollend', finishSnap);
          track.classList.remove('is-snapping');
        };
        track.addEventListener('scrollend', finishSnap);
        track.scrollTo({ left: targetLeft, behavior: 'smooth' });
      },
    });

    let frame = null;
    track.addEventListener(
      'scroll',
      () => {
        if (track.classList.contains('is-snapping')) return;
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          const { clientWidth, scrollLeft } = track;
          if (clientWidth) setActive(Math.round(scrollLeft / clientWidth));
        });
      },
      { passive: true }
    );
  };

  const closePickers = (except = null) => {
    document.querySelectorAll('[data-mx-qa-list]').forEach((list) => {
      if (list === except) return;
      list.hidden = true;
      list.parentElement
        ?.querySelector('[data-mx-qa-toggle]')
        ?.setAttribute('aria-expanded', 'false');
    });
  };

  const openPicker = (toggle, direction = 'start') => {
    const list = toggle.parentElement?.querySelector('[data-mx-qa-list]');
    if (!list) return;

    closePickers(list);
    list.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');

    const enabled = [...list.querySelectorAll('[data-mx-qa-option]:not([aria-disabled="true"])')];
    const selected = list.querySelector('[data-mx-qa-option][aria-selected="true"]:not([aria-disabled="true"])');
    const target = direction === 'end' ? enabled.at(-1) : selected ?? enabled[0];
    target?.focus();
  };

  const readVariants = (root) => {
    const node = root.querySelector('[data-mx-qa-variants]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  };

  const selectOption = (option) => {
    const root = option.closest('[data-mx-qa]');
    if (!root || option.getAttribute('aria-disabled') === 'true') return;

    const variants = readVariants(root);
    const value = option.dataset.value;
    const variant = variants?.find((v) => v.value === value);
    if (!variant) return;

    const label = root.querySelector('[data-mx-qa-value]');
    const button = root.querySelector('[data-mx-qa-btn]');
    const list = root.querySelector('[data-mx-qa-list]');
    const toggle = root.querySelector('[data-mx-qa-toggle]');

    if (label) label.textContent = value;

    root.querySelectorAll('[data-mx-qa-option]').forEach((item) => {
      item.setAttribute('aria-selected', item === option ? 'true' : 'false');
    });

    const input = root.querySelector('[data-mx-qa-variant-input]');
    if (input) input.value = String(variant.id);

    if (button) {
      button.toggleAttribute('data-unavailable', !variant.available);
      button.disabled = !variant.available;
    }

    if (list) list.hidden = true;
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }
  };

  /*
   * Moved to <body> so no ancestor can trap it. A parent with transform,
   * filter or contain turns position: fixed into position relative to that
   * parent, which is what let the header and third-party widgets cover it.
   */
  const detachDrawers = () => {
    document.querySelectorAll('[data-mx-filter-drawer]').forEach((drawer) => {
      if (drawer.parentElement !== document.body) document.body.appendChild(drawer);
    });
  };

  const triggerFor = (drawer) =>
    document.querySelector(`[aria-controls="${drawer.id}"]`);

  const closeTimers = new WeakMap();
  const sectionCache = new Map();
  let activeCollectionRequest = null;
  let growaveDocumentObserver = null;
  let growaveObserverTimeout = null;
  let cardRowFrame = null;
  let cardRowsBound = false;

  const syncCardRows = () => {
    const grids = document.querySelectorAll('[data-mx-grid]');

    grids.forEach((grid) => {
      const cards = [...grid.querySelectorAll(':scope > [data-mx-card]')];
      cards.forEach((card) => card.classList.remove('mx-card--stock-row', 'mx-card--sold-row'));
      grid.classList.remove('mx-collection__grid--card-rows-ready');

      const hasQuickAdd = cards.some((card) => card.querySelector('[data-mx-qa]'));
      if (!hasQuickAdd) return;

      const rows = new Map();
      cards.forEach((card) => {
        const top = Math.round(card.getBoundingClientRect().top);
        const row = rows.get(top) ?? [];
        row.push(card);
        rows.set(top, row);
      });

      rows.forEach((row) => {
        if (row.some((card) => card.querySelector('.mx-stock-badge'))) {
          row.forEach((card) => card.classList.add('mx-card--stock-row'));
        }
        if (row.some((card) => card.querySelector('.mx-sold__num'))) {
          row.forEach((card) => card.classList.add('mx-card--sold-row'));
        }
      });

      grid.classList.add('mx-collection__grid--card-rows-ready');
    });
  };

  const queueCardRowSync = () => {
    if (cardRowFrame) cancelAnimationFrame(cardRowFrame);
    cardRowFrame = requestAnimationFrame(() => {
      cardRowFrame = null;
      syncCardRows();
    });
  };

  const initCardRows = () => {
    syncCardRows();
    if (cardRowsBound) return;

    cardRowsBound = true;
    window.addEventListener('resize', queueCardRowSync, { passive: true });
  };

  // Growave renders counters in open shadow roots, outside theme CSS scope.
  const GROWAVE_CARD_STYLE_ID = 'mx-growave-card-a11y';
  const GROWAVE_CARD_STYLE = `
    .gw-toggle-wishlist-product-card-button__counter {
      background-color: #d91f42 !important;
    }
  `;

  const applyGrowaveCardOverrides = () => {
    let pending = false;

    document.querySelectorAll('.mx-card .gw-add-to-wishlist-product-card-placeholder').forEach((host) => {
      const shadowRoot = host.shadowRoot;
      if (!shadowRoot) {
        pending = true;
        return;
      }

      if (!shadowRoot.getElementById(GROWAVE_CARD_STYLE_ID)) {
        const style = document.createElement('style');
        style.id = GROWAVE_CARD_STYLE_ID;
        style.textContent = GROWAVE_CARD_STYLE;
        shadowRoot.append(style);
      }
    });

    if (!pending) {
      growaveDocumentObserver?.disconnect();
      growaveDocumentObserver = null;
      clearTimeout(growaveObserverTimeout);
      growaveObserverTimeout = null;
    }

    return !pending;
  };

  const initGrowaveCards = () => {
    if (applyGrowaveCardOverrides() || growaveDocumentObserver) return;

    growaveDocumentObserver = new MutationObserver(applyGrowaveCardOverrides);
    growaveDocumentObserver.observe(document.documentElement, { childList: true, subtree: true });

    clearTimeout(growaveObserverTimeout);
    growaveObserverTimeout = setTimeout(() => {
      applyGrowaveCardOverrides();
      growaveDocumentObserver?.disconnect();
      growaveDocumentObserver = null;
      growaveObserverTimeout = null;
    }, 10000);
  };

  const trapDrawerFocus = (drawer, event) => {
    const focusable = [...drawer.querySelectorAll(
      'a[href]:not([aria-disabled="true"]), button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
    )].filter((element) => {
      const closedDetails = element.closest('details:not([open])');
      const isClosedSummary = closedDetails
        && element === closedDetails.querySelector(':scope > summary');

      return !element.hidden
        && (!closedDetails || isClosedSummary)
        && element.getClientRects().length > 0;
    });

    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const setDrawer = (drawer, open) => {
    if (!drawer) return;

    clearTimeout(closeTimers.get(drawer));
    document.documentElement.classList.toggle('mx-filter-drawer-open', open);

    const trigger = triggerFor(drawer);
    trigger?.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (open) {
      drawer.hidden = false;
      drawer.querySelector('.mx-filter-drawer__close')?.focus();
      // Unhide first, then flip the class on the next frame so the browser
      // has a start value to transition from.
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
      });
      return;
    }

    drawer.classList.remove('is-open');
    trigger?.focus();

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    closeTimers.set(
      drawer,
      setTimeout(() => {
        drawer.hidden = true;
      }, reduced ? 0 : 300)
    );
  };

  const preserveTestParams = (url) => {
    const current = new URL(window.location.href);
    current.searchParams.forEach((value, key) => {
      const preserve = key === 'preview_theme_id'
        || key === 'pb'
        || key === '_fd'
        || key.startsWith('ig');
      if (preserve && !url.searchParams.has(key)) url.searchParams.set(key, value);
    });
    url.searchParams.delete('section_id');
    return url;
  };

  const formUrl = (form) => {
    const url = new URL(form.action || window.location.pathname, window.location.href);
    url.search = new URLSearchParams(new FormData(form)).toString();
    return preserveTestParams(url);
  };

  const publicUrl = (href) => preserveTestParams(new URL(href, window.location.href));

  const rememberCollectionState = (source) => {
    const openFacets = {};
    document.querySelectorAll('[data-mx-facets]').forEach((form) => {
      openFacets[form.dataset.mxFacets] = [...form.querySelectorAll('[data-mx-facet-param][open]')]
        .map((facet) => facet.dataset.mxFacetParam);
    });

    const drawer = document.querySelector('[data-mx-filter-drawer].is-open');
    const sourceForm = source instanceof Element
      ? source.closest('[data-mx-facets], [data-mx-sort]')
      : null;

    let focus = null;
    if (source instanceof HTMLInputElement) {
      focus = {
        type: 'input',
        context: sourceForm?.dataset.mxFacets,
        name: source.name,
        value: source.value,
      };
    } else if (source instanceof HTMLSelectElement) {
      focus = { type: 'sort' };
    }

    return {
      drawerOpen: Boolean(drawer),
      drawerScrollTop: drawer?.querySelector('.mx-filter-drawer__body')?.scrollTop || 0,
      focus,
      openFacets,
      sidebarScrollTop: document.querySelector('.mx-collection__sidebar')?.scrollTop || 0,
    };
  };

  const restoreCollectionState = (root, state) => {
    document.querySelectorAll('[data-mx-facets]').forEach((form) => {
      const open = state.openFacets[form.dataset.mxFacets];
      if (!open) return;
      form.querySelectorAll('[data-mx-facet-param]').forEach((facet) => {
        facet.open = open.includes(facet.dataset.mxFacetParam);
      });
    });

    const drawer = document.getElementById(`mx-filter-drawer-${root.dataset.mxSectionId}`);
    if (state.drawerOpen && drawer) {
      drawer.hidden = false;
      drawer.classList.add('is-open');
      document.documentElement.classList.add('mx-filter-drawer-open');
      triggerFor(drawer)?.setAttribute('aria-expanded', 'true');
    }

    requestAnimationFrame(() => {
      const sidebar = document.querySelector('.mx-collection__sidebar');
      if (sidebar) sidebar.scrollTop = state.sidebarScrollTop;

      const drawerBody = drawer?.querySelector('.mx-filter-drawer__body');
      if (drawerBody) drawerBody.scrollTop = state.drawerScrollTop;

      if (state.focus?.type === 'sort') {
        root.querySelector('[data-mx-sort] select')?.focus();
      } else if (state.focus?.type === 'input') {
        const form = document.querySelector(`[data-mx-facets="${state.focus.context}"]`);
        const input = [...(form?.elements || [])].find((element) =>
          element instanceof HTMLInputElement
          && element.name === state.focus.name
          && element.value === state.focus.value
        );
        input?.focus();
      }
    });
  };

  const fetchCollectionSection = async (url, sectionId, signal) => {
    const requestUrl = new URL(url);
    requestUrl.searchParams.set('section_id', sectionId);
    const cacheKey = requestUrl.toString();

    if (sectionCache.has(cacheKey)) return sectionCache.get(cacheKey);

    const response = await fetch(requestUrl, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal,
    });
    if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);

    const html = await response.text();
    sectionCache.set(cacheKey, html);
    if (sectionCache.size > 8) sectionCache.delete(sectionCache.keys().next().value);
    return html;
  };

  const renderCollection = async (href, { source = null, updateHistory = true, scroll = false } = {}) => {
    const currentRoot = document.querySelector('[data-mx-collection]');
    const sectionId = currentRoot?.dataset.mxSectionId;
    if (!currentRoot || !sectionId) {
      window.location.assign(href);
      return;
    }

    const url = publicUrl(href);
    const state = rememberCollectionState(source);
    const oldDrawer = document.getElementById(`mx-filter-drawer-${sectionId}`);

    activeCollectionRequest?.abort();
    const controller = new AbortController();
    activeCollectionRequest = controller;

    currentRoot.classList.add('is-loading');
    currentRoot.setAttribute('aria-busy', 'true');
    oldDrawer?.setAttribute('aria-busy', 'true');

    try {
      const html = await fetchCollectionSection(url, sectionId, controller.signal);
      if (controller.signal.aborted) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fetchedRoot = parsed.querySelector('[data-mx-collection]');
      if (!fetchedRoot) throw new Error('Collection section markup was missing');

      const nextRoot = document.importNode(fetchedRoot, true);
      oldDrawer?.remove();
      currentRoot.replaceWith(nextRoot);
      boot();
      restoreCollectionState(nextRoot, state);

      if (updateHistory) {
        history.pushState({ mxCollection: true }, '', `${url.pathname}${url.search}${url.hash}`);
      }

      const status = nextRoot.querySelector('[data-mx-status]');
      if (status) status.textContent = `${nextRoot.dataset.mxProductCount} products loaded`;

      if (scroll) {
        requestAnimationFrame(() => {
          const top = nextRoot.getBoundingClientRect().top + window.scrollY - 110;
          window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
        });
      }

      window.Growave?.refresh?.();
      document.dispatchEvent(new CustomEvent('mx:collection:updated'));
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(url);
    } finally {
      if (activeCollectionRequest === controller) activeCollectionRequest = null;
      currentRoot.classList.remove('is-loading');
      currentRoot.removeAttribute('aria-busy');
      oldDrawer?.removeAttribute('aria-busy');
    }
  };

  const showMore = async (button, grid) => {
    const next = button.dataset.nextUrl;
    const sectionId = button.closest('[data-mx-collection]')?.dataset.mxSectionId;
    if (!next || !sectionId || button.hasAttribute('aria-busy')) return;

    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Loading…';

    try {
      const html = await fetchCollectionSection(publicUrl(next), sectionId);
      const doc = new DOMParser().parseFromString(html, 'text/html');

      doc.querySelectorAll('[data-mx-grid] [data-mx-card]').forEach((card) => {
        const node = document.importNode(card, true);
        grid.appendChild(node);
        initCarousel(node);
      });

      const nextUrl = doc.querySelector('[data-mx-show-more]')?.dataset.nextUrl;
      if (nextUrl) {
        button.dataset.nextUrl = nextUrl;
        button.removeAttribute('aria-busy');
        button.textContent = 'Show more';
      } else {
        button.remove();
      }

      // Growave only scans the DOM on load, so appended cards need a nudge.
      window.Growave?.refresh?.();
      queueCardRowSync();
    } catch {
      button.removeAttribute('aria-busy');
      button.textContent = 'Show more';
    }
  };

  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const option = target.closest('[data-mx-qa-option]');
    if (option) {
      e.preventDefault();
      e.stopPropagation();
      selectOption(option);
      return;
    }

    const toggle = target.closest('[data-mx-qa-toggle]');
    if (toggle) {
      e.preventDefault();
      e.stopPropagation();
      const list = toggle.parentElement?.querySelector('[data-mx-qa-list]');
      if (!list) return;
      const willOpen = list.hidden;
      if (willOpen) {
        openPicker(toggle);
      } else {
        list.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      }
      return;
    }

    if (target.closest('[data-unavailable]')) {
      e.preventDefault();
      return;
    }

    const descToggle = target.closest('[data-mx-desc-toggle]');
    if (descToggle) {
      const desc = descToggle.closest('[data-mx-desc]');
      if (!desc) return;
      const isExpanded = desc.hasAttribute('data-expanded');
      desc.toggleAttribute('data-expanded');
      descToggle.setAttribute('aria-expanded', String(!isExpanded));
      descToggle.textContent = isExpanded ? 'Show more' : 'Show less';
      return;
    }

    const openDrawer = target.closest('[data-mx-filter-drawer-open]');
    if (openDrawer) {
      setDrawer(document.getElementById(openDrawer.getAttribute('aria-controls')), true);
      return;
    }

    const closeDrawer = target.closest('[data-mx-filter-drawer-close]');
    if (closeDrawer) {
      setDrawer(closeDrawer.closest('[data-mx-filter-drawer]'), false);
      return;
    }

    const more = target.closest('[data-mx-show-more]');
    if (more) {
      const grid = more.closest('[data-mx-collection]')?.querySelector('[data-mx-grid]');
      if (grid) showMore(more, grid);
      return;
    }

    const collectionLink = target.closest(
      '.mx-pagination__link[href], .mx-facets__clear[href], .mx-collection__empty a[href]'
    );
    if (
      collectionLink
      && !e.defaultPrevented
      && e.button === 0
      && !e.metaKey
      && !e.ctrlKey
      && !e.shiftKey
      && !e.altKey
    ) {
      e.preventDefault();
      renderCollection(collectionLink.href, {
        source: collectionLink,
        scroll: Boolean(collectionLink.closest('.mx-pagination')),
      });
      return;
    }

    closePickers();
  });

  document.addEventListener('keydown', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const option = target.closest('[data-mx-qa-option]');
    if (option) {
      const list = option.closest('[data-mx-qa-list]');
      const enabled = list
        ? [...list.querySelectorAll('[data-mx-qa-option]:not([aria-disabled="true"])')]
        : [];
      const index = enabled.indexOf(option);

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const offset = e.key === 'ArrowDown' ? 1 : -1;
        enabled[(index + offset + enabled.length) % enabled.length]?.focus();
        return;
      }
      if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        enabled[e.key === 'Home' ? 0 : enabled.length - 1]?.focus();
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        selectOption(option);
        return;
      }
    }

    const toggle = target.closest('[data-mx-qa-toggle]');
    if (toggle && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      openPicker(toggle, e.key === 'ArrowUp' ? 'end' : 'start');
      return;
    }

    const drawer = document.querySelector('[data-mx-filter-drawer]:not([hidden])');
    if (drawer && e.key === 'Tab') {
      trapDrawerFocus(drawer, e);
      return;
    }

    if (e.key !== 'Escape') return;

    const openList = document.querySelector('[data-mx-qa-list]:not([hidden])');
    const pickerToggle = openList?.parentElement?.querySelector('[data-mx-qa-toggle]');
    closePickers();
    pickerToggle?.focus();
    document.querySelectorAll('[data-mx-filter-drawer]:not([hidden])').forEach((openDrawer) => {
      setDrawer(openDrawer, false);
    });
  });

  // Facet and sort forms still work as normal GET forms without JavaScript.
  // With JavaScript, Shopify renders only this section and the URL/history are
  // updated without a full page reload.
  document.addEventListener('change', (e) => {
    const form = e.target instanceof Element
      ? e.target.closest('[data-mx-facets], [data-mx-sort]')
      : null;
    if (form) renderCollection(formUrl(form), { source: e.target });
  });

  document.addEventListener('submit', (e) => {
    const form = e.target instanceof HTMLFormElement
      ? e.target.closest('[data-mx-facets], [data-mx-sort]')
      : null;
    if (!form) return;
    e.preventDefault();
    renderCollection(formUrl(form), { source: document.activeElement });
  });

  const checkDescClamping = () => {
    document.querySelectorAll('[data-mx-desc]').forEach((desc) => {
      const text = desc.querySelector('[data-mx-desc-text]');
      const toggle = desc.querySelector('[data-mx-desc-toggle]');
      if (!text || !toggle || desc.hasAttribute('data-expanded')) return;
      const clamped = text.scrollHeight > text.clientHeight + 2;
      desc.classList.toggle('mx-collection__desc--clamped', clamped);
      toggle.hidden = !clamped;
    });
  };

  const initDescs = () => {
    requestAnimationFrame(checkDescClamping);
  };

  const boot = () => {
    detachDrawers();
    document.querySelectorAll('[data-mx-card]').forEach(initCarousel);
    initGrowaveCards();
    initCardRows();
    initDescs();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

  window.addEventListener('load', checkDescClamping, { once: true });

  window.addEventListener('popstate', () => {
    if (!document.querySelector('[data-mx-collection]')) return;
    renderCollection(window.location.href, { updateHistory: false });
  });
}
